// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { classNames, numberToFixed } from '@shared/ui-kit/utils';
import { useCheckbox } from '@shared/ui-kit/hooks';
import { portfolioPositionsSlice } from '@shared/data-access/slices/portfolio-positions';
import { useAppDispatch } from '@advisor-app/store/store';
import { Checkbox, Tooltip } from '@mui/material';
import { type Stock } from '@shared/data-access/interfaces/api/ai/screener';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import { type TargetAsset } from '@shared/data-access/interfaces/redux/portfolio-rebalance-state';
import {
  AssetBrand,
  Heading,
  IconButton,
  OptionsSelector,
  Table,
  DeleteButton,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';
import { type FC, useCallback, useMemo } from 'react';
import { InputWeightPercentage } from './input-weight-percentage';
import { RebalanceAddPosition } from './rebalance-add-position';
import { useInvestmentPopup } from '@shared/tag-management/components/investment-popup';
import { useTranslation } from 'react-i18next';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';

export interface RebalancePortfolioEditorProps {
  readonly data: TargetAsset[];
  readonly portfolios: string[];
  readonly onChangePortfolioSelected: (newPortfolioSelected: string) => void;
  readonly portfolioSelected: string;
  readonly rebalanceType: string;
  readonly investment: number;
  readonly rebalanceCash: number;
  readonly currency: AccountCurrency;
}

export const RebalancePortfolioEditor: FC<RebalancePortfolioEditorProps> = ({
  data,
  portfolios,
  onChangePortfolioSelected,
  portfolioSelected,
  rebalanceType,
  investment,
  rebalanceCash,
  currency,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { openInvestmentPopup } = useInvestmentPopup();

  const totalWeight = useMemo(
    () =>
      +data
        .map(asset => asset.weight)
        .reduce((a, b) => a + b, 0)
        .toFixed(2),
    [data],
  );

  const tickers = useMemo(() => data.map(t => t.ticker), [data]);

  const {
    selected: selectedTickers,
    toggleCheckbox,
    clearSelection,
    isAllSelected,
    selectAll,
  } = useCheckbox(tickers);

  const targetInvestment = useMemo(
    () =>
      rebalanceType === 'withdrawal'
        ? investment - rebalanceCash
        : investment + rebalanceCash,
    [investment, rebalanceCash, rebalanceType],
  );

  const onChangeWeight = useCallback(
    (obj: TargetAsset, previousWeight: number) => {
      /* istanbul ignore next */
      if (obj.weight === previousWeight) return;

      const availableWeight = totalWeight - previousWeight;

      let finalWeight = obj.weight;

      if (obj.weight + availableWeight > 100) {
        finalWeight = numberToFixed(previousWeight + (100 - totalWeight));
      }
      /* istanbul ignore next */
      if (finalWeight !== previousWeight) {
        const payload = {
          ticker: obj.ticker,
          asset_class: obj.asset_class,
          weight: finalWeight,
          details: obj.details,
        };
        dispatch(
          portfolioPositionsSlice.actions.updateTargetPortfolioPosition(payload),
        );
      }
    },
    [dispatch, totalWeight],
  );

  const onDeleteSelectedTickers = useCallback(() => {
    dispatch(
      portfolioPositionsSlice.actions.deleteTargetPortfolioPosition(
        selectedTickers,
      ),
    );

    clearSelection();
  }, [clearSelection, dispatch, selectedTickers]);

  const onDeletePosition = useCallback(
    (ticker: string) => {
      dispatch(
        portfolioPositionsSlice.actions.deleteTargetPortfolioPosition([ticker]),
      );

      toggleCheckbox(ticker);
    },
    [dispatch, toggleCheckbox],
  );

  return (
    <div className="w-full">
      <div className="mb-9 flex flex-col">
        <div className="mb-7 flex justify-between">
          <Heading
            title={t('rebalance.select-target-portfolio', { ns: 'rebalance' })}
            variant="h3"
          />
          <Tooltip
            title={t('rebalance.delete-selected-tickers', { ns: 'rebalance' })}
          >
            <span>
              <IconButton
                icon="Trashcan"
                variant="button"
                disabled={!selectedTickers.length}
                onClick={onDeleteSelectedTickers}
                data-testid="delete-selected-tickers"
              />
            </span>
          </Tooltip>
        </div>
        <div className="grid grid-cols-[repeat(2,minmax(0,_200px))] justify-between gap-x-10 xl:grid-cols-[repeat(2,minmax(200px,256px))]">
          <RebalanceAddPosition />
          <OptionsSelector
            optionsAvailable={portfolios!}
            onChangeValueSelected={onChangePortfolioSelected!}
            optionSelected={portfolioSelected}
            defaultText={t('rebalance.select-portfolio', { ns: 'rebalance' })}
            selectType="white"
          />
        </div>
      </div>
      <table className="w-full bg-section text-base uppercase leading-6 text-border-dark">
        <thead>
          <tr className="[&>th>div]:mb-4 [&>th]:font-normal">
            <th>
              <div className="flex gap-x-2">
                <Checkbox
                  key="check-all"
                  checked={isAllSelected && !!selectedTickers.length}
                  className="!p-0"
                  onChange={() => (isAllSelected ? clearSelection() : selectAll())}
                  disabled={!data.length}
                  data-testid="check-all"
                />
                <span>
                  <Translation i18nKey="common.name" ns="common" />
                </span>
              </div>
            </th>
            <th>
              <div>
                <Translation i18nKey="common.ticker" ns="common" />
              </div>
            </th>
            <th>
              <div>
                <Translation i18nKey="common.isin" ns="common" />
              </div>
            </th>
            <th>
              <div>
                <Translation i18nKey="common.investment" ns="common" />
              </div>
            </th>
            <th className="w-[100px]">
              <div>
                <Translation i18nKey="common.weight" ns="common" />
              </div>
            </th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody className="overflow-y-scroll">
          {data.map(({ ticker, weight, asset_class, details }) => (
            <tr
              key={ticker}
              className="text-primary-medium [&>td>div]:mb-4 [&>td>div]:h-[24px]"
            >
              <td>
                <div className="flex items-center gap-x-2">
                  <Checkbox
                    key="check-all"
                    checked={selectedTickers.includes(ticker)}
                    className="!p-0"
                    onChange={() => toggleCheckbox(ticker)}
                    data-testid={`checkbox-${ticker}`}
                  />
                  <div className="max-w-[200px] xl:max-w-32 2xl:max-w-[200px]">
                    <AssetBrand
                      ticker={ticker}
                      label={details?.name}
                      logoUrl={(details as Stock)?.url_logo}
                      url={(details as Stock)?.url_morningstar}
                      assetType={details?.asset_type ?? 'stock'}
                      analyzePopup={openInvestmentPopup}
                      watchlistButton={
                        <WatchlistToggleButton
                          ticker={ticker}
                          assetType={details?.asset_type ?? 'stock'}
                        />
                      }
                    />
                  </div>
                </div>
              </td>
              <td>
                <div>
                  <span data-testid={`ticker-${ticker}`}>
                    {details?.ticker ?? ticker}
                  </span>
                </div>
              </td>
              <td>
                <div>
                  <span>{details?.isin ?? '-'}</span>
                </div>
              </td>
              <td>
                <div className="flex gap-x-2">
                  <Table.Data
                    value={(targetInvestment * weight) / 100}
                    format="currency"
                    formatOptions={{
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                      currency: currency,
                    }}
                  />
                </div>
              </td>
              <td>
                <div>
                  <div className="flex h-[20px] items-center justify-center overflow-visible">
                    <InputWeightPercentage
                      ticker={ticker}
                      asset_class={asset_class}
                      weight={Math.round(weight * 100) / 100}
                      onChange={value =>
                        value !== null &&
                        onChangeWeight(
                          { ticker, weight: value!, asset_class, details },
                          Math.round(weight * 100) / 100,
                        )
                      }
                      totalWeight={totalWeight}
                      data-testid={`input-${ticker}`}
                    />
                  </div>
                </div>
              </td>
              <td>
                <div className="mx-4 flex justify-center hover:!text-primary">
                  <DeleteButton
                    onClick={() => onDeletePosition(ticker)}
                    data-testid={`delete-${ticker}`}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6}>
              <hr className="mb-4 border-sidebar-text" />
            </td>
          </tr>
          <tr>
            <td>Total</td>
            <td colSpan={3}></td>
            <td
              className={classNames('font-semibold text-primary', {
                'text-negative': totalWeight !== 100,
              })}
            >
              <Table.Data
                value={totalWeight / 100}
                format="percentage"
                formatOptions={{
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 1,
                }}
              />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
